import type React from 'react';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';
import type { IndicationsBoxController } from '@/components/boxes/indicationsBoxes/indicationBoxController.ts';
import type { BoolFunction } from '@/components/boxes/hooks/types.ts';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { getConditionalStyles } from '@/components/boxes/styles.ts';
import { StyledTableCell } from '@/components/boxes/BoxTools.tsx';
import { UpdatableValue } from '@/components/boxes/common/UpdatableValue.tsx';
import { EyeCheckboxButton, PenCheckboxButton } from '@/components/boxes/CheckboxButton';

import { useTheme } from '@mui/material/styles';
import { IconButton, Tooltip } from '@mui/material';
import {
  AnnotationIndicationIcon,
  AreaIndicationIcon,
  SplineIndicationIcon,
  DistanceIndicationIcon,
  AngleIndicationIcon
} from '@/components/boxes/box-containers/boxContainers.ts';

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { type IBaseViewModel, SceneItemKind } from '@/engine/app/interfaces.ts';
import { EditableCellTextField } from '@/components/common/EditableCellTextField.tsx';
import { observer } from 'mobx-react';
import { AngleModel } from '@/engine/app/modules/indications/angle/Angle.model.ts';
import { DistanceModel } from '@/engine/app/modules/indications/distance/Distance.model.ts';
import type { Props as UpdatableValueProps } from '@/components/boxes/common/UpdatableValue.tsx';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { ValueConstructor } from '@/engine/DataFlow/DataFlow';

const indicatedMeasurements = [
  SceneItemKind.IndicatedDistance,
  SceneItemKind.IndicatedSplineMeasurement,
  SceneItemKind.IndicatedAreaMeasurement,
  SceneItemKind.IndicatedAngle
] as const;

type IndicatedMeasurementKind = (typeof indicatedMeasurements)[number];

type MeasurementKind = DistanceModel | DT.SplineMeasurement | DT.AreaMeasurement | AngleModel;

type CellProps<T extends IValue> = Pick<UpdatableValueProps<T>, 'valueKey' | 'constructor'> & {
  unit: string;
};

interface CellPropsByMeasurementKind {
  [SceneItemKind.IndicatedDistance]: CellProps<DistanceModel>;
  [SceneItemKind.IndicatedSplineMeasurement]: CellProps<DT.SplineMeasurement>;
  [SceneItemKind.IndicatedAreaMeasurement]: CellProps<DT.AreaMeasurement>;
  [SceneItemKind.IndicatedAngle]: CellProps<AngleModel>;
}

const measurementProps: CellPropsByMeasurementKind = {
  [SceneItemKind.IndicatedDistance]: {
    valueKey: 'distance',
    constructor: DistanceModel,
    unit: 'mm'
  },
  [SceneItemKind.IndicatedSplineMeasurement]: {
    valueKey: 'length',
    constructor: DT.SplineMeasurement,
    unit: 'mm'
  },
  [SceneItemKind.IndicatedAreaMeasurement]: {
    valueKey: 'area',
    constructor: DT.AreaMeasurement,
    unit: ' mm²'
  },
  [SceneItemKind.IndicatedAngle]: {
    valueKey: 'angleDeg',
    constructor: AngleModel,
    unit: '°'
  }
};

interface Props {
  indication: IBaseViewModel;
  mainController: IndicationsBoxController;
  isSelected: BoolFunction;
  isHighlighted: BoolFunction;
  index: number;
  isImplicitlyEditable: boolean;
}

export const IndicationRow: React.FC<Props> = observer(
  ({ indication, mainController, isSelected, isHighlighted, index, isImplicitlyEditable: implicitlyEditable }) => {
    const theme = useTheme();

    const isItemSelected: boolean = isSelected(indication.descriptor);
    const isItemHighlighted: boolean = isHighlighted(indication.descriptor);

    const indicationKind = indication.kind as IndicatedMeasurementKind;
    const isMeasurement = indicatedMeasurements.includes(indicationKind);

    let unit, valueKey, constructor;
    if (isMeasurement) {
      ({ unit, valueKey, constructor } = measurementProps[indicationKind]);
    }

    return (
      <TableRow
        key={index}
        data-testid={indication.descriptor + 'Distance'}
        sx={{
          ...{
            '&:last-child td, &:last-child th': { border: 0 },
            '&:hover button.locate-button': {
              opacity: 1
            },
            '& button.locate-button': { opacity: 0.1 }
          },
          ...getConditionalStyles(
            isItemHighlighted,
            theme,
            { paddingLeft: 0.3, paddingTop: 0, paddingBottom: 0 },
            {
              paddingRight: 0.5,
              paddingTop: 0,
              paddingBottom: 0
            }
          )
        }}
        onMouseEnter={(): void => {
          indication.setHighlighting(true);
        }}
        onMouseLeave={(): void => {
          indication.setHighlighting(false);
        }}
        selected={isItemSelected}
        onClick={(): void => {
          mainController.setIndicationSelection(indication.descriptor, !isItemSelected);
        }}
      >
        <StyledTableCell
          align="center"
          key={'criteria' + index}
          className="limited-width cell-height"
          component="td"
          scope="row"
        >
          {indication.kind === SceneItemKind.IndicatedAnnotation && <AnnotationIndicationIcon fontSize={'small'} />}
          {indication.kind === SceneItemKind.IndicatedAreaMeasurement && <AreaIndicationIcon fontSize={'small'} />}
          {indication.kind === SceneItemKind.IndicatedSplineMeasurement && <SplineIndicationIcon fontSize={'small'} />}
          {indication.kind === SceneItemKind.IndicatedDistance && <DistanceIndicationIcon fontSize={'small'} />}
          {indication.kind === SceneItemKind.IndicatedAngle && <AngleIndicationIcon fontSize={'small'} />}
        </StyledTableCell>
        <TableCell
          colSpan={isMeasurement ? 1 : 2}
          component="td"
          align={'left'}
          scope="row"
          key={'label' + index}
          sx={{
            color: theme.mode === 'dark' ? 'inherit' : theme.palette.text.primary
          }}
        >
          <EditableCellTextField
            value={indication.label}
            onBlur={(e): void => {
              mainController.setLabel(indication.descriptor, e.currentTarget.value);
            }}
          />
        </TableCell>
        {isMeasurement && (
          <TableCell
            size="small"
            align="right"
            key={'value' + index}
            sx={{
              '&': {
                paddingRight: 0,
                width: '6rem',
                color: theme.mode === 'dark' ? 'inherit' : theme.palette.text.primary
              }
            }}
          >
            <UpdatableValue
              descriptor={indication.descriptor}
              valueKey={valueKey as keyof MeasurementKind}
              constructor={constructor as ValueConstructor}
            />
            <span>{` ${unit}`}</span>
          </TableCell>
        )}
        <StyledTableCell
          align="right"
          key={'delete' + index}
          className="limited-width"
          onClick={(event): void => event.stopPropagation()}
        >
          <Tooltip title="Delete" placement="left" hidden={indication.imported}>
            <span>
              <IconButton
                size="small"
                color="primary"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
                onClick={async (): Promise<void> => {
                  await mainController.deleteIndication(indication);
                }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </StyledTableCell>
        {!implicitlyEditable && (
          <StyledTableCell
            align="right"
            key={'beingEdited' + index}
            className="limited-width"
            onClick={(event): void => event.stopPropagation()}
          >
            {indication.isPickable && indication.isTunable ? (
              <PenCheckboxButton
                onChange={(checked: boolean): void => {
                  mainController.setIndicationBeingEdited(indication.descriptor, checked);
                }}
                checked={mainController.isIndicationBeingEdited(indication.descriptor)}
              />
            ) : (
              <div />
            )}
          </StyledTableCell>
        )}
        <StyledTableCell
          align="right"
          key={'visibility' + index}
          className="limited-width"
          onClick={(event): void => event.stopPropagation()}
        >
          <EyeCheckboxButton
            onChange={(checked: boolean): void => {
              mainController.setIndicationVisibility(indication.descriptor, checked);
            }}
            checked={indication.isVisible}
          />
        </StyledTableCell>
      </TableRow>
    );
  }
);
